import { useState, useEffect } from 'react';
import { 
  MagnifyingGlassIcon, 
  FunnelIcon,
  UserIcon,
  EnvelopeIcon,
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import useUserManagement from './useUserManagement';

export default function UserManagement() {
  const {
    users,
    loading,
    totalUsers,
    currentPage,
    totalPages,
    filters,
    setFilters,
    setCurrentPage,
    searchUsers,
    clearFilters,
    viewUser,
    editUser,
    deleteUser,
    blockUser,
    unblockUser
  } = useUserManagement();

  const [searchTerm, setSearchTerm] = useState(filters.search || '');
  const [selectedPlan, setSelectedPlan] = useState(filters.planId || '');
  const [selectedStatus, setSelectedStatus] = useState(filters.status || '');


  // Dummy plan options - replace with actual API data
  const planOptions = [
    { value: '', label: 'All Plans' },
    { value: '68a5f1463e361d7eebb8b6f2', label: 'Free' },
    { value: '68a5f1463e361d7eebb8b6f3', label: 'Standard' },
    { value: '68a5f1463e361d7eebb8b6f4', label: 'Prime' },
    { value: '68a5f1463e361d7eebb8b6f5', label: 'Elite' },
  ];


  const statusOptions = [
    { value: '', label: 'All Users' },
    { value: 'true', label: 'Active' },
    { value: 'false', label: 'Inactive' },
  ];

  const handleSearch = () => {
    setFilters({
      search: searchTerm,
      planId: selectedPlan,
      status: selectedStatus,
    });
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedPlan('');
    setSelectedStatus('');
    clearFilters();
  };

  const handlePageChange = (page) => {
    console.log("Changing to page:", page);
    setCurrentPage(page);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-white">User Management</h1>
        <div className="text-sm text-gray-400">
          Total Users: <span className="text-primaryBlue font-semibold">{totalUsers}</span>
        </div>
      </div>

      {/* Search and Filters Row */}
      <div className=" py-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search Bar */}
          <div className="relative ">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-4 border-borderGray border bg-blueGray rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primaryBlue"
            />
          </div>

          {/* Plan Type Filter */}
          <div className='' >
            <select
              value={selectedPlan}
              onChange={(e) => setSelectedPlan(e.target.value)}
              className="w-full px-4 py-4  border-borderGray border bg-blueGray rounded-lg text-white focus:outline-none focus:border-primaryBlue"
            >
              {planOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-4 py-4 bg-blueGray border border-borderGray rounded-lg text-white focus:outline-none focus:border-primaryBlue"
            >
              {statusOptions.map((option) => (
                <option className=' border border-borderGray' key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <button
              onClick={handleSearch}
              className="flex items-center gap-2 px-4 py-4 bg-blueGray border border-borderGray  hover:bg-blueGray/50 text-white rounded-lg transition-colors"
            >
              <MagnifyingGlassIcon className="h-4 w-4" />
              Search
            </button>
            <button
              onClick={handleClearFilters}
              className="flex items-center gap-2 px-4 py-4 bg-blueGray border border-borderGray  hover:bg-blueGray/50 text-white rounded-lg transition-colors"
            >
              <FunnelIcon className="h-4 w-4" />
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className=" border border-borderGray rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-blueGray border-b border-borderGray">
              <tr>
                <th className="px-6 py-4 text-left text-base font-medium text-gray-300 uppercase tracking-wider border-r border-borderGray">
                  User
                </th>
                <th className="px-6 py-4 text-left text-base font-medium text-gray-300 uppercase tracking-wider border-r border-borderGray">
                  Email
                </th>
                <th className="px-6 py-4 text-left text-base font-medium text-gray-300 uppercase tracking-wider border-r border-borderGray">
                  Plan
                </th>
                {/* <th className="px-6 py-4 text-left text-base font-medium text-gray-300 uppercase tracking-wider">
                  Join Date
                </th> */}
                <th className="px-6 py-4 text-left text-base font-medium text-gray-300 uppercase tracking-wider border-r border-borderGray">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-base font-medium text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-borderGray">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-400">
                    Loading users...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-400">
                    No users found
                  </td>
                </tr>
              ) : (
                users?.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-700/50">
                    <td className="px-6 py-4 whitespace-nowrap border-r border-borderGray">
                      <div className="flex items-center">
                        {/* <div className="h-10 w-10 rounded-full bg-primaryBlue flex items-center justify-center">
                          <UserIcon className="h-6 w-6 text-white" />
                        </div> */}
                        <div className="">
                          <div className="text-sm font-medium text-white">
                            {user.name || 'N/A'}
                          </div>
                          {/* <div className="text-sm text-gray-400">
                            ID: {user.id}
                          </div> */}
                        </div>
                      </div>
                    </td>
                    <td className="px-6   py-4 whitespace-nowrap border-r border-borderGray">
                      <div className=" ">
                        {/* <EnvelopeIcon className="h-4 w-4 text-gray-400 mr-2" /> */}
                        <span className="font-medium text-white text-sm  ">{user.email}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 border-r border-borderGray whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm   text-white font-medium">
                        {user?.activeSubscription?.planId?.name ||user?.activeSubscription?.[0]?.planId?.name|| 'Free'}
                      </span>
                    </td>
                    {/* <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-300">
                          {user.joinDate ? new Date(user.joinDate).toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                    </td> */}
                    <td className="px-6 py-4 border-r border-borderGray whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${
                        user.isActive
                          ? 'bg-green-900 text-white' 
                          : 'bg-red-900 text-white'
                      }`}>
                        {user.isActive  ? (
                          <CheckCircleIcon className="h-3 w-3 mr-1" />
                        ) : (
                          <XCircleIcon className="h-3 w-3 mr-1" />
                        )}
                        {user.isActive? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => viewUser(user._id)}
                          className="text-white text-sm font-medium bg-primaryBlue px-3 py-1 rounded-sm transition-colors"
                          title="View User"
                        >
                          View Detail
                        </button>
                    
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-blueGray border-2 border-borderGray rounded-lg p-4">
          <div className="text-sm text-gray-400">
            Showing page {currentPage || 1} of {totalPages || 1} ({totalUsers || 0} total users)
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => handlePageChange((currentPage || 1) - 1)}
              disabled={(currentPage || 1) === 1}
              className="px-3 py-2 bg-gray-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 transition-colors"
            >
              Previous
            </button>
            
            {/* Page Numbers with better logic */}
            {(() => {
              const pages = [];
              const maxVisiblePages = 5;
              const current = currentPage || 1;
              const total = totalPages || 1;
              let startPage = Math.max(1, current - Math.floor(maxVisiblePages / 2));
              let endPage = Math.min(total, startPage + maxVisiblePages - 1);
              
              // Adjust start page if we're near the end
              if (endPage - startPage + 1 < maxVisiblePages) {
                startPage = Math.max(1, endPage - maxVisiblePages + 1);
              }
              
              // First page
              if (startPage > 1) {
                pages.push(
                  <button
                    key={1}
                    onClick={() => handlePageChange(1)}
                    className="px-3 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    1
                  </button>
                );
                if (startPage > 2) {
                  pages.push(
                    <span key="ellipsis1" className="px-2 py-2 text-gray-400">...</span>
                  );
                }
              }
              
              // Middle pages
              for (let i = startPage; i <= endPage; i++) {
                pages.push(
                  <button
                    key={i}
                    onClick={() => handlePageChange(i)}
                    className={`px-3 py-2 rounded-lg transition-colors ${
                      current === i
                        ? 'bg-primaryBlue text-white'
                        : 'bg-gray-700 text-white hover:bg-gray-600'
                    }`}
                  >
                    {i}
                  </button>
                );
              }
              
              // Last page
              if (endPage < total) {
                if (endPage < total - 1) {
                  pages.push(
                    <span key="ellipsis2" className="px-2 py-2 text-gray-400">...</span>
                  );
                }
                pages.push(
                  <button
                    key={total}
                    onClick={() => handlePageChange(total)}
                    className="px-3 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    {total}
                  </button>
                );
              }
              
              return pages;
            })()}
            
            <button
              onClick={() => handlePageChange((currentPage || 1) + 1)}
              disabled={(currentPage || 1) === (totalPages || 1)}
              className="px-3 py-2 bg-gray-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
